// src/pages/Dashboard.jsx
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../context/SnackbarContext";
import { jwtDecode } from "jwt-decode";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import QrCode from "../components/patients/modals/QrCode";
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { getPetOwnerById } from "../api/petOwnerService";
import PetOwnerView from "../components/patients/modals/PetOwnerView";
import PetOwnerForm from "../components/patients/forms/PetOwnerForm";
import UpcomingAppointmentsList from "../components/history/lists/UpcomingAppointmentsList";
import LowStockInventoryItemsList from "../components/inventory/lists/LowStockInventoryItemsList";
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import { useNavigate } from "react-router-dom";
import AppointmentsCalendar from "../components/history/display/AppointmentsCalendar";
import AppointmentsCalendarDesktop from "../components/history/display/AppointmentsCalendarDesktop";

const Dashboard = () => {
    const { t } = useTranslation('common');
    const { showSnackbar } = useSnackbar();

    const baseApiUrl = process.env.REACT_APP_API_URL;
    const publicUploadsFolder = "public_uploads";
    const token = localStorage.getItem("accessToken");
    const officeId = jwtDecode(token).active_office_id;

    const [petOwnerFormOpen, setPetOwnerFormOpen] = React.useState(false);
    const [petFormMode, setPetFormMode] = React.useState('create');
    const [selectedPetOwner, setSelectedPetOwner] = React.useState(null);
    const [petOwnerViewOpen, setPetOwnerViewOpen] = React.useState(false);

    const handleAdd = () => {
        setSelectedPetOwner(null);
        setPetFormMode("create");
        setPetOwnerFormOpen(true);
    };

    const handleEdit = () => {
        setPetFormMode("edit");
        setPetOwnerFormOpen(true);
    };

    const onSavePetOwnerSuccess = async (savedPetOwner) => {
        const updatedPetOwner = await getPetOwnerById(savedPetOwner.id);
        setSelectedPetOwner(updatedPetOwner);
        if (!petOwnerViewOpen) setPetOwnerViewOpen(true);
    };

    const navigate = useNavigate();
    const handleMedicalHistoryClick = () => {
        navigate("/history");
    };

    // ✅ Detect desktop viewport (lg and up)
    const isDesktop = useMediaQuery('(min-width:1024px)');

    return (
        <>
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ flexGrow: 1, marginBottom: "0rem" }}>
              {localStorage.getItem('activeOfficeName')}
          </Typography>
          <Box
              component="img"
              src={`${baseApiUrl}/${publicUploadsFolder}/logo-${officeId}.png`}
              alt="logo"
              sx={{
                  width: "100%",
                  maxWidth: 500,
                  display: "block",
                  margin: "0 auto",
              }}
              onError={(e) => {
                  e.currentTarget.src = "/images/logo-0.png";
              }}
          />
        
          <List>
              <ListItem>
                <ListItemButton onClick={handleMedicalHistoryClick}>
                  <ListItemIcon>
                      <ContentPasteSearchIcon fontSize='large' color='secondary' />
                  </ListItemIcon>
                  <ListItemText 
                      primary="Patient Medical History"
                      secondary="search patient medical history"
                  />
              </ListItemButton>
              </ListItem>
              <QrCode />
              <ListItem>
                  <ListItemButton onClick={handleAdd}>
                  <ListItemIcon>
                      <PersonAddAlt1Icon fontSize='large' color='secondary' />
                  </ListItemIcon>
                  <ListItemText 
                      primary="New Patient"
                      secondary="create new pet owner and pet(s) record"
                  />
                  </ListItemButton>
              </ListItem>
          </List>

          {/* ✅ Conditional calendar rendering */}
          {isDesktop ? <AppointmentsCalendarDesktop /> : <AppointmentsCalendar />}

          <UpcomingAppointmentsList mode='dashboard' />
          <LowStockInventoryItemsList mode='dashboard' />
        </Stack>

        <PetOwnerView 
            handleEdit={handleEdit}
            setPetOwners={null}
            petOwner={selectedPetOwner}
            open={petOwnerViewOpen}
            onClose={() => setPetOwnerViewOpen(false)}
        />
        <PetOwnerForm 
            successCallback={onSavePetOwnerSuccess}
            setPetOwners={null} 
            petOwner={selectedPetOwner}
            mode={petFormMode}
            open={petOwnerFormOpen}
            onClose={() => setPetOwnerFormOpen(false)}
        />
        </>
    );
};

export default Dashboard;
